import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  TableContainer,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TextField,
  InputAdornment,
  TableSortLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { getProductById } from "utils/api/private/ProductApiService";
import { Product } from "utils/types/ProductTypes";

interface GroupedRow {
  id: string;
  productId: string;
  fullname?: string;
  soldPriceUSD?: number;
  quantity: number;
  quantitySold: number;
  quantityNotSold: number;
}

interface GroupedGiftCardsTableProps {
  groupedGiftCards: GroupedRow[];
  products: Product[]; // optional seed cache (ignored for price freshness)
}

const GroupedGiftCardsTable: React.FC<GroupedGiftCardsTableProps> = ({
  groupedGiftCards,
  products,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(40);
  const [searchQuery, setSearchQuery] = useState("");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<"product" | "sellingPrice">("product");

  // We always refresh prices when rows change:
  const [productMap, setProductMap] = useState<Record<string, Product>>({});

  // Seed names etc. from props once (optional—price still fetched fresh below)
  useEffect(() => {
    if (!products?.length) return;
    setProductMap((prev) => {
      const next = { ...prev };
      for (const p of products) next[p.id] = p;
      return next;
    });
  }, [products]);

  // FETCH PRICES EVERY TIME groupedGiftCards CHANGES
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const ids = Array.from(new Set(groupedGiftCards.map((g) => g.productId)));
      if (ids.length === 0) return;

      const results = await Promise.allSettled(
        ids.map((id) => getProductById(id))
      );

      if (cancelled) return;

      const fresh: Record<string, Product> = {};
      results.forEach((res) => {
        if (res.status === "fulfilled" && res.value) {
          fresh[res.value.id] = res.value; // contains latest priceUSD
        }
      });

      if (Object.keys(fresh).length) {
        // overwrite to ensure freshest prices are used
        setProductMap((prev) => ({ ...prev, ...fresh }));
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [groupedGiftCards]);

  const getProduct = useCallback(
    (productId: string) => productMap[productId],
    [productMap]
  );

  // Prefer soldPriceUSD; otherwise use freshly fetched product priceUSD
  const getProductName = useCallback(
    (productId: string) => {
      const p = getProduct(productId);
      return p
        ? `${p.name} ${p.price} ${p.currency}`
        : `Unknown Product (${productId})`;
    },
    [getProduct]
  );

  // Prefer row.soldPriceUSD; else current product.priceUSD; else 0
  const getRowSellingPrice = useCallback(
    (row: GroupedRow): number => {
      if (
        typeof row.soldPriceUSD === "number" &&
        !Number.isNaN(row.soldPriceUSD)
      ) {
        return row.soldPriceUSD;
      }
      const p = getProduct(row.productId);
      return typeof p?.priceUSD === "number" ? p.priceUSD : 0;
    },
    [getProduct]
  );
  // Filter by product label (sync)
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return groupedGiftCards;
    return groupedGiftCards.filter((row) =>
      getProductName(row.productId).toLowerCase().includes(q)
    );
  }, [groupedGiftCards, searchQuery, getProductName]);

  // Sort by product label OR computed selling price (sync)
  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (orderBy === "product") {
      arr.sort((a, b) => {
        const A = getProductName(a.productId);
        const B = getProductName(b.productId);
        return order === "asc" ? A.localeCompare(B) : B.localeCompare(A);
      });
    } else {
      arr.sort((a, b) => {
        const A = getRowSellingPrice(a);
        const B = getRowSellingPrice(b);
        return order === "asc" ? A - B : B - A;
      });
    }
    return arr;
  }, [filtered, orderBy, order, getProductName, getRowSellingPrice]);

  const handleRequestSort = (prop: "product" | "sellingPrice") => {
    const isAsc = orderBy === prop && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(prop);
  };

  return (
    <TableContainer component={Paper} sx={{ marginTop: 4 }}>
      <Typography variant="h6">Grouped Gift Cards</Typography>

      <TextField
        variant="outlined"
        placeholder="Search by Product..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ marginBottom: 2, width: "30%" }}
      />

      <Table>
        <TableHead>
          <TableRow>
            <TableCell sortDirection={orderBy === "product" ? order : false}>
              <TableSortLabel
                active={orderBy === "product"}
                direction={orderBy === "product" ? order : "asc"}
                onClick={() => handleRequestSort("product")}
              >
                Product
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={orderBy === "sellingPrice" ? order : false}
            >
              <TableSortLabel
                active={orderBy === "sellingPrice"}
                direction={orderBy === "sellingPrice" ? order : "asc"}
                onClick={() => handleRequestSort("sellingPrice")}
              >
                Selling Price (USD)
              </TableSortLabel>
            </TableCell>
            <TableCell>Quantity</TableCell>
            <TableCell>Quantity Sold</TableCell>
            <TableCell>Quantity Not Sold</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {sorted
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((row, idx) => (
              <TableRow key={`${row.productId}-${idx}`}>
                <TableCell>{row.fullname}</TableCell>
                <TableCell>
                  {getRowSellingPrice(row)
                    ? getRowSellingPrice(row).toFixed(2)
                    : "—"}
                </TableCell>
                <TableCell>{row.quantity}</TableCell>
                <TableCell>{row.quantitySold}</TableCell>
                <TableCell>{row.quantityNotSold}</TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 40, 50, 100]}
        component="div"
        count={filtered.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </TableContainer>
  );
};

export default GroupedGiftCardsTable;
